import React, { FormEvent } from 'react';
import { useContextSelector } from 'use-context-selector';

import { PatientForm } from './Components/PatientForm';

const alertEinsteinIcon =
  'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/warningBlue.svg';

import * as S from './styles';
import { INITIAL_VALUES, formPatientHasError } from './utils/validateForm';
import { useFormik } from 'formik';
import { RouterContext } from '../../../../contexts/RouterContext';
import { ScheduleContext } from '../../../../contexts/ScheduleContext';
import { PatientContext, PatientProps } from '../../../../contexts/PatientContext';
import { patientSchema } from '../../../../Schemas/patient';
import { PAGES } from '../../../../Router/pages';
import { ScheduleSpecialistBuilder } from '../../../../models/ScheduleSpecialist';
import { maskCpf, maskDateStringIso, maskPassport, maskPhone, removeMask } from '../../../../utils/masks';
import { isOlderThan, isYoungerThan } from '../../../../utils/validators';
import HeadTitle from '../../../../components/HeadTitle';
import TagInfo from '../../../../components/TagInfo';
import FooterWrapper from '../../../../components/FooterWrapper';
import { Button, LoadingOverlay } from '../../../../components';
import { SpecialistContext } from '../../../../contexts/SpecialistContext';
import AgeGroupModal from '../../components/AgeGroupModal';
import { useMainRouter } from '../../../../Router/pageComponents';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { FirebaseEvents } from '../../../../utils/enums/firebase-events';
import { useServiceContext } from '../../../../contexts/ServiceContext';

const PatientIdentification: React.FC = () => {
  const { goTo, goBack } = useContextSelector(RouterContext, state => state);

  const { changeScheduleSpecialist } = useContextSelector(ScheduleContext, state => state);

  const { addPatientList, setPatientSelected, patientSelected } = useContextSelector(PatientContext, state => state);

  const { selectedSpecialist, setSelectedSpecialist } = useContextSelector(SpecialistContext, state => state);

  const { selectedService } = useServiceContext();

  const [loading, setLoading] = React.useState(false);

  const [isAgeGroupModalVisible, setIsAgeGroupModalVisible] = React.useState(false);

  const { logEvent } = useAnalytics();

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: patientSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async values => {
      setLoading(true);

      const unitsFiltered = filterUnitsToSchedule(values.birthDate);

      if (!unitsFiltered?.length) {
        setIsAgeGroupModalVisible(true);
      }

      if (unitsFiltered?.length) {
        if (selectedSpecialist) setSelectedSpecialist({ ...selectedSpecialist, units: unitsFiltered });

        const isFormValid = !formPatientHasError({
          values,
          formik,
          checkGenericRulesCatalog: () => undefined
        });

        const patient = handleRawPatientData(values);
        const patientCreated = addPatientList(patient);

        setPatientSelected(patientCreated);

        if (isFormValid && patientCreated.idPatient) {
          logEvent({
            eventName: FirebaseEvents.schedule_selected_patient,
            eventParams: {
              service_id: selectedService.id,
              service_name: selectedService.name,
              patient_nationality: patient.citizenship,
              patient_documentType: patient.documentType,
              patient_gender: patient.gender,
              patient_birth: patient.birthDate
              // location: ''
            }
          });

          makeSpecialistSchedule({
            patientId: patientCreated.idPatient
          });

          goTo(PAGES.SERVICE_TYPE_PAGE.id);
        }
      }

      setLoading(false);
    }
  });

  const filterUnitsToSchedule = (birthDate: string) => {
    return selectedSpecialist?.units.filter(unit => {
      if (
        unit.schedule_types?.some(type => type.name === 'Autoagendamento') &&
        unit?.external_platform?.code_company &&
        unit?.external_platform?.code_professional
      ) {
        const AGE_GROUP_RULE_FROM = unit?.rules?.find(
          rule => rule.rule_id === process.env.REACT_APP_AGE_GROUP_FROM_RULEID
        )?.value;

        const AGE_GROUP_RULE_UP_TO = unit?.rules?.find(
          rule => rule.rule_id === process.env.REACT_APP_AGE_GROUP_UP_TO_RULEID
        )?.value;

        const ageGroupFrom = Number(AGE_GROUP_RULE_FROM);
        const ageGroupUpTo = Number(AGE_GROUP_RULE_UP_TO);

        return isInRangeGroup({ birthDate: birthDate, ageGroupFrom, ageGroupUpTo });
      }
      return false;
    });
  };

  const isInRangeGroup = (props: { ageGroupFrom: number; ageGroupUpTo: number; birthDate: string }): boolean => {
    const { ageGroupFrom, ageGroupUpTo, birthDate } = props;

    if (ageGroupFrom && ageGroupUpTo)
      return isOlderThan(birthDate, ageGroupFrom) && isYoungerThan(birthDate, ageGroupUpTo);

    if (ageGroupFrom && !ageGroupUpTo) return isOlderThan(birthDate, ageGroupFrom);

    if (!ageGroupFrom && ageGroupUpTo) return isYoungerThan(birthDate, ageGroupUpTo);

    if (!ageGroupFrom && !ageGroupUpTo) return true;

    return false;
  };

  function makeSpecialistSchedule(props: { patientId: string }) {
    const scheduleSpecialist = ScheduleSpecialistBuilder();
    scheduleSpecialist.setPatient(props.patientId);
    changeScheduleSpecialist(scheduleSpecialist.build());
  }

  function handleRawPatientData(patientData: typeof INITIAL_VALUES): PatientProps {
    const patient = {} as PatientProps;
    patient.name = patientData.name.trim();
    patient.email = patientData.email.trim();

    patient.document = removeMask(patientData.document);
    patient.phone = removeMask(patientData.phone);

    if (!isOlderThan(patientData.birthDate, 9)) patient.pregnant = false;
    else patient.pregnant = patientData.pregnant === 'Sim';

    return { ...patientData, ...patient };
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    formik.submitForm();
  };

  function checkButtonEnable(): boolean {
    const errors = formik.errors;

    const values: { [index: string]: string } = formik.values;

    const requiredFields = ['document', 'documentType', 'name', 'email', 'birthDateDate', 'phone'];

    const hasEmptyFields = requiredFields.some(field => !values[field]);

    const hasErrors = Object.values(errors).some(error => !!error);

    return hasEmptyFields || hasErrors;
  }

  function updateFormPatient(patient: PatientProps) {
    formik.setValues({
      ...patient,
      document: patient.documentType === 'CPF' ? maskCpf(patient.document) : maskPassport(patient.document),
      phone: maskPhone(patient.phone),
      birthDateDate: maskDateStringIso(patient.birthDate) || '',
      pregnant: patient.gender === 'Feminino' ? (patient.pregnant ? 'Sim' : 'Não') : '',
      gender: patient.gender === 'não informado' ? '' : patient.gender,
      idPatient: patient.idPatient || ''
    });
  }

  React.useEffect(() => {
    if (patientSelected) {
      updateFormPatient(patientSelected);
    }
  }, []);

  const { goBack: mainRouterGoBack } = useMainRouter();

  function handleGoBack() {
    return goBack();
  }

  return (
    <S.Container onSubmit={e => handleSubmit(e)}>
      <S.FieldsWrapper>
        <HeadTitle
          title="Informe os dados do paciente"
          goBack={() => {
            handleGoBack();
          }}
        />
        <div className="info-wrapper">
          <TagInfo
            icon={<img src={alertEinsteinIcon} alt="alert icon" />}
            info="Os dados do paciente são confidenciais e de uso exclusivo dos serviços Einstein."
          />
        </div>

        <PatientForm formik={formik} />
      </S.FieldsWrapper>

      <FooterWrapper>
        <S.ButtonWrapper>
          <Button title="Continuar" type="button" disabled={checkButtonEnable()} onClick={() => formik.submitForm()} />
        </S.ButtonWrapper>
      </FooterWrapper>
      <LoadingOverlay loading={loading} />

      {/* <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => {
          setIsModalSystemUnavailableVisible(false);
        }}
      /> */}

      <AgeGroupModal visible={isAgeGroupModalVisible} onClose={() => setIsAgeGroupModalVisible(value => !value)} />
    </S.Container>
  );
};

export default PatientIdentification;
